import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Modal, Spin } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useConnection } from '../../connection/connection';
import { useEvents } from '../../common/EventContext';
import { setFormValidationErrors, Utils } from '../../common/Utils';
import { useBusinessContext } from '../../context/BusinessContext';
import { PettyCash } from '../../dto/PettyCash';

export type PettyCashEditModalInstance = {
  create: () => void;
  edit: (warehouseId: number) => void;
};

type Props = {};

const PettyCashEditModal: ForwardRefRenderFunction<
  PettyCashEditModalInstance,
  Props
> = (props, ref) => {
  const [title, setTitle] = useState<string>('Create Petty Cash Account');
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm<PettyCash>();
  const events = useEvents();
  const connection = useConnection();
  const business = useBusinessContext();
  const [existingPettyCash, setExistingPettyCash] = useState<PettyCash>();

  useImperativeHandle(
    ref,
    () => ({
      create: () => {
        setTitle('Create Petty Cash Account');
        setVisible(true);
        setSaving(false);
        setExistingPettyCash(undefined);
        form.resetFields();
      },
      edit: (pettyCashId: number) => {
        setTitle('Edit Petty Cash Account');
        setVisible(true);
        setSaving(false);
        setLoading(true);
        form.resetFields();
        form.setFieldsValue({});
        connection
          .get<PettyCash>(`${business.businessId}/petty-cash/${pettyCashId}`)
          .then((res) => {
            form.setFieldsValue(res.data);
            setExistingPettyCash(res.data);
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            Modal.error({ title: 'Error', content: 'Something went wrong!' });
            setVisible(false);
          });
      },
    }),
    [business.businessId, connection, form],
  );

  const schema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'name',
        type: 'string',
        name: 'Name',
        props: { placeholder: 'Name' },
        options: {
          rules: [{ required: true, message: 'Name is required' }],
        },
      },
      {
        id: 'siteId',
        type: 'select2',
        name: 'Site',
        props: {
          placeholder: 'Site',
          options: business.select.sites,
          showSearch: true,
          optionFilterProp: 'label',
          disabled: !!existingPettyCash,
        },
        options: {
          rules: [{ required: true, message: 'Site is required' }],
        },
      },
      {
        id: 'balance',
        type: 'price',
        name: !existingPettyCash ? 'Amount' : 'Balance',
        props: {
          disabled: !!existingPettyCash,
        },
        options: {
          rules: [{ required: true, message: 'Balance is required' }],
        },
      },
      {
        id: 'userId',
        type: 'user',
        name: 'User',
        props: { placeholder: 'User' },
      },
    ];
    if (existingPettyCash) {
      formSchema.push({
        id: 'status',
        type: 'select',
        name: 'Status',
        props: { placeholder: 'Status', options: ['Active', 'Inactive'] },
        options: {
          rules: [{ required: true, message: 'Status is required' }],
        },
      });
    }
    return formSchema;
  }, [business.select.sites, existingPettyCash]);

  const submit = useCallback(
    (values: PettyCash) => {
      setSaving(true);
      if (existingPettyCash) {
        connection
          .patch<PettyCash>(
            `${business.businessId}/petty-cash/${existingPettyCash.pettyCashId}`,
            values,
          )
          .then((res) => {
            events.emit('PETTYCASH', { action: 'UPDATE', data: res.data });
            setVisible(false);
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      } else {
        connection
          .post<PettyCash>(`${business.businessId}/petty-cash`, values)
          .then((res) => {
            events.emit('PETTYCASH', { action: 'CREATE', data: res.data });
            setVisible(false);
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      }
    },
    [business.businessId, connection, events, existingPettyCash, form],
  );

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          <div className="w-full flex justify-end gap-1">
            <Button onClick={() => setVisible(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={form.submit}>
              OK
            </Button>
          </div>
        </div>
      }
      width={600}
    >
      <Spin spinning={loading}>
        <Form onFinish={submit} form={form} layout="vertical">
          <Form.Items schema={schema} />
        </Form>
      </Spin>
    </Drawer>
  );
};

export const usePettyCashEditModal = () =>
  useRef<PettyCashEditModalInstance>(null);

export default React.forwardRef<PettyCashEditModalInstance, Props>(
  PettyCashEditModal,
);
